/* eslint-disable no-underscore-dangle */
import { Message } from '@twilio/conversations';

interface IMessageCollectionConfig {
  getParentMessageId: (message) => string | undefined;
}

export class MessagesCollection<TMessage extends Message> {
  private readonly _replies: Map<TMessage['sid'], Array<TMessage>>;
  private readonly _messages: Array<TMessage>;
  private readonly _config: IMessageCollectionConfig;

  constructor(config: IMessageCollectionConfig) {
    this._messages = [];
    this._replies = new Map<TMessage['sid'], Array<TMessage>>();
    this._config = config;
  }

  public add(message: Array<TMessage> | TMessage) {
    if (Array.isArray(message)) {
      this.addMany(message);
    } else {
      this.addOne(message);
    }
  }

  public update(message: TMessage) {
    const parentMessageId = this._config.getParentMessageId(message);

    if (parentMessageId) {
      const currentReplies = this._replies.get(parentMessageId);
      const replyIndex = currentReplies.findIndex((m) => m.sid === message.sid);

      currentReplies.splice(replyIndex, 1, message);
      this._replies.set(parentMessageId, currentReplies);
    } else {
      const index = this._messages.findIndex((m) => m.sid === message.sid);

      this._messages.splice(index, 1, message);
    }
  }

  public remove(message: TMessage) {
    const parentMessageId = this._config.getParentMessageId(message);

    if (parentMessageId) {
      const currentReplies = this._replies.get(parentMessageId);

      this._replies.set(
        parentMessageId,
        currentReplies.filter((m) => m.sid !== message.sid)
      );
    } else {
      const index = this._messages.findIndex((m) => m.sid === message.sid);

      this._messages.splice(index, 1);
    }
  }

  private addMany(messages: Array<TMessage>) {
    messages.reverse().forEach((message) => {
      const parentMessageId = this._config.getParentMessageId(message);

      if (parentMessageId) {
        const currentReplies = this._replies.get(parentMessageId) || [];

        this._replies.set(parentMessageId, [...currentReplies, message]);
      } else {
        this._messages.unshift(message);
      }
    });
  }

  private addOne(message: TMessage) {
    const parentMessageId = this._config.getParentMessageId(message);

    if (parentMessageId) {
      const currentReplies = this._replies.get(parentMessageId) || [];

      this._replies.set(parentMessageId, [message, ...currentReplies]);
    } else {
      this._messages.push(message);
    }
  }

  get items() {
    const sortedMessages = this._messages.reduce((acc, message) => {
      const currentReplies = this._replies.get(message.sid);

      if (currentReplies) {
        return [...currentReplies, message, ...acc];
      }
      return [message, ...acc];
    }, []);

    return sortedMessages;
  }
}
